import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Users, AlertCircle, CheckCircle, Lightbulb } from 'lucide-react';

const TeamBuilderModal = ({ users, onClose }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);

  const dimensionNames = {
    B: 'Brainstorm',
    U: 'Uncover',
    I: 'Inspect',
    L: 'Lift',
    D: 'Deliver',
    R: 'Rally'
  };

  const dimensionColors = {
    B: 'bg-purple-500',
    U: 'bg-blue-500',
    I: 'bg-green-500',
    L: 'bg-pink-500',
    D: 'bg-orange-500',
    R: 'bg-red-500'
  };

  // Get all users who have completed assessments
  const completedUsers = users.filter(u => u.assessment_result);

  // Calculate dimension distribution in volunteer pool
  const dimensionCounts = {
    B: completedUsers.filter(u => u.assessment_result.primary_dimension === 'B').length,
    U: completedUsers.filter(u => u.assessment_result.primary_dimension === 'U').length,
    I: completedUsers.filter(u => u.assessment_result.primary_dimension === 'I').length,
    L: completedUsers.filter(u => u.assessment_result.primary_dimension === 'L').length,
    D: completedUsers.filter(u => u.assessment_result.primary_dimension === 'D').length,
    R: completedUsers.filter(u => u.assessment_result.primary_dimension === 'R').length
  };

  // Calculate selected team composition
  const selectedDimensions = selectedUsers.map(u => u.assessment_result.primary_dimension);
  const teamCounts = {
    B: selectedDimensions.filter(d => d === 'B').length,
    U: selectedDimensions.filter(d => d === 'U').length,
    I: selectedDimensions.filter(d => d === 'I').length,
    L: selectedDimensions.filter(d => d === 'L').length,
    D: selectedDimensions.filter(d => d === 'D').length,
    R: selectedDimensions.filter(d => d === 'R').length
  };

  const missingDimensions = Object.keys(dimensionNames).filter(d => teamCounts[d] === 0);

  const toggleUser = (user) => {
    if (selectedUsers.find(u => u.user.id === user.user.id)) {
      setSelectedUsers(selectedUsers.filter(u => u.user.id !== user.user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const getComplementaryPairings = () => {
    const pairings = {
      'B': ['U', 'D', 'I'],
      'U': ['B', 'D'],
      'I': ['U', 'R'],
      'L': ['D', 'R'],
      'D': ['B', 'U', 'L'],
      'R': ['I', 'L', 'D']
    };
    return pairings;
  };

  const suggestComplement = (dimension) => {
    const complements = getComplementaryPairings()[dimension];
    return complements.map(d => dimensionNames[d]).join(', ');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-8">
          <div className="mb-8">
            <h2 className="text-4xl font-bold text-gray-800 mb-2">Team Builder</h2>
            <p className="text-xl text-gray-600">Build balanced teams with complementary BUILDR profiles</p>
          </div>

          {/* Volunteer Pool Overview */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-6 h-6" />
                Your Volunteer Pool Overview
              </CardTitle>
              <CardDescription>Distribution of BUILDR strengths across all volunteers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(dimensionCounts).map(([dim, count]) => (
                  <div key={dim} className="text-center">
                    <div className={`w-20 h-20 ${dimensionColors[dim]} rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className="text-3xl font-bold text-white">{count}</span>
                    </div>
                    <div className="text-sm font-semibold">{dimensionNames[dim]}</div>
                    {count === 0 && (
                      <Badge variant="destructive" className="text-xs mt-1">Missing</Badge>
                    )}
                  </div>
                ))}
              </div>

              {Object.values(dimensionCounts).some(c => c === 0) && (
                <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-orange-800">Missing Dimensions</p>
                      <p className="text-orange-700 text-sm">
                        Consider recruiting volunteers with these strengths: {' '}
                        {Object.entries(dimensionCounts)
                          .filter(([_, count]) => count === 0)
                          .map(([dim]) => dimensionNames[dim])
                          .join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Selection */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Build Your Team</CardTitle>
              <CardDescription>Select volunteers to see team composition and recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Click on volunteers below to add them to your team</p>
              ) : (
                <div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedUsers.map(user => (
                      <Badge
                        key={user.user.id}
                        className="text-sm px-3 py-2 cursor-pointer hover:opacity-80"
                        onClick={() => toggleUser(user)}
                      >
                        {user.user.name} ({dimensionNames[user.assessment_result.primary_dimension]})
                        <X className="w-3 h-3 ml-2" />
                      </Badge>
                    ))}
                  </div>

                  {/* Team Analysis */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        Team Strengths
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(teamCounts)
                          .filter(([_, count]) => count > 0)
                          .map(([dim, count]) => (
                            <div key={dim} className="flex items-center gap-2">
                              <div className={`w-3 h-3 ${dimensionColors[dim]} rounded-full`}></div>
                              <span>{dimensionNames[dim]}: {count}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        {missingDimensions.length > 0 ? (
                          <AlertCircle className="w-5 h-5 text-orange-600" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        {missingDimensions.length > 0 ? 'Missing in Team' : 'Complete Team!'}
                      </h4>
                      {missingDimensions.length > 0 ? (
                        <div className="space-y-2">
                          {missingDimensions.map(dim => (
                            <div key={dim} className="text-orange-700">
                              • {dimensionNames[dim]}
                            </div>
                          ))}
                          <p className="text-sm text-gray-600 mt-3">
                            Consider adding volunteers with these strengths for better balance
                          </p>
                        </div>
                      ) : (
                        <p className="text-green-700">Your team has all BUILDR dimensions covered!</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Complementary Pairing Guide */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-6 h-6 text-yellow-600" />
                Complementary Pairing Guide
              </CardTitle>
              <CardDescription>Which BUILDR types work best together</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(dimensionNames).map(([dim, name]) => (
                  <div key={dim} className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 ${dimensionColors[dim]} rounded-full`}></div>
                      <span className="font-semibold">{name}</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Works well with: <span className="font-medium">{suggestComplement(dim)}</span>
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Available Volunteers */}
          <Card>
            <CardHeader>
              <CardTitle>Available Volunteers ({completedUsers.length})</CardTitle>
              <CardDescription>Click to add to team</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {completedUsers.map(user => {
                  const isSelected = selectedUsers.find(u => u.user.id === user.user.id);
                  return (
                    <div
                      key={user.user.id}
                      onClick={() => toggleUser(user)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold">{user.user.name}</div>
                          <div className="text-sm text-gray-600">{user.user.email}</div>
                        </div>
                        <Badge className={dimensionColors[user.assessment_result.primary_dimension] + ' text-white'}>
                          {dimensionNames[user.assessment_result.primary_dimension]}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 flex justify-end">
            <Button onClick={onClose} className="bg-indigo-600 hover:bg-indigo-700">
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamBuilderModal;
